/**
 * Всё состояние игры — один документ. Определения предметов и зон сюда не копируются,
 * только идентификаторы из контент-паков.
 */

/** Поднимать при любом изменении формы документа и писать миграцию. */
export const SAVE_VERSION = 9;

export type LossMode = 'mamkin' | 'pacanski';

export type GearSlotKey = 'kostyum' | 'obuv' | 'cep' | 'persten' | 'barsetka' | 'voda' | 'partak';

export interface PlayerState {
  level: number;
  xp: number;
  /** Нераспределённые очки статов: по два за уровень. */
  statPoints: number;
  /** Класс с пятого уровня, подкласс с пятнадцатого. Выбор необратим. */
  classId: string | null;
  subclassId: string | null;
  /** Статы из карты. */
  sila: number;
  smekalochka: number;
  obayanie: number;
  chotkost: number;
  avtoritet: number;
  /** Шкалы 0–100. */
  stress: number;
  health: number;
  heat: number;
  /** Бабки в копейках: деньги никогда не хранятся дробными. */
  cash: number;
  zone: string;
  tile: { x: number; y: number };
}

export interface LogEntry {
  day: number;
  minute: number;
  text: string;
  /** Изменение денег в копейках, если было. */
  money?: number;
}

/** Краденое: не тратится, а сбывается в приёмке. Занимает место в руках. */
export interface HabarItem {
  def: string;
  title: string;
  /** Полная стоимость в копейках; приёмка снимет комиссию. */
  value: number;
}

/** Спутница: снимает стресс за подарки, без внимания уходит. */
export interface Companion {
  def: string;
  title: string;
  tier: number;
  stressRelief: number;
  giftPrice: number;
  /** День последней прогулки: по нему считается терпение. */
  lastDateDay: number;
}

/** Точка дохода: капает за ночь, пока пацан спит. */
export interface Business {
  def: string;
  title: string;
  zone: string;
  level: number;
  incomePerHour: number;
  /** Производство светит: палево набегает за ночь. */
  heatPerNight: number;
}

/** Пацан из бригады: сила в драке и доля с дохода. */
export interface BrigadeSlot {
  member: string;
  title: string;
  sila: number;
  chotkost: number;
  health: number;
  share: number;
}

/** Территория: с какого дня держим. */
export interface HeldTerritory {
  id: string;
  heldSince: number;
}

/** Состояние квеста: активен на каком шаге или уже закрыт. */
export interface QuestState {
  state: 'active' | 'done';
  step: number;
}

export interface WorldState {
  /**
   * Что игрок уже трогал: сама расстановка мира выводится из сида и дня,
   * поэтому храним только следы вмешательства.
   */
  touched: string[];
}

/** Следствие и чем закончилось прохождение. */
export interface JusticeState {
  raids: number;
  lastRaidDay: number;
  courtDay: number | null;
}

export interface SaveDoc {
  version: number;
  /** Мир детерминирован от сида: тот же сид — тот же двор. */
  seed: string;
  /** Режим выбирается при старте новой игры и не меняется по ходу. */
  mode: LossMode;
  day: number;
  /** Минуты, прошедшие с начала игрового дня (0…960). */
  minute: number;
  player: PlayerState;
  habar: HabarItem[];
  businesses: Business[];
  companions: Companion[];
  /** Репутация с NPC, −100…100. */
  npc: Record<string, number>;
  quests: Record<string, QuestState>;
  /** Счётчики для квестов и телеметрии: сколько дел, сбытов, драк. */
  counters: Record<string, number>;
  brigade: BrigadeSlot[];
  territories: HeldTerritory[];
  justice: JusticeState;
  /** Пока active — играем. Иначе прохождение закрыто. */
  outcome: 'active' | 'srok' | 'mogila';
  /** Купленный шмот: идентификаторы предметов из контент-пака. */
  owned: string[];
  /** Купленные тачки и какая из них под задницей. */
  garage: string[];
  activeVehicle: string | null;
  /** Что надето, по слотам. */
  equipment: Partial<Record<GearSlotKey, string>>;
  world: WorldState;
  log: LogEntry[];
}

export function createSave(seed: string, mode: LossMode = 'mamkin'): SaveDoc {
  return {
    version: SAVE_VERSION,
    seed,
    mode,
    day: 1,
    minute: 0,
    player: {
      level: 1,
      xp: 0,
      statPoints: 0,
      classId: null,
      subclassId: null,
      sila: 3,
      smekalochka: 3,
      obayanie: 3,
      chotkost: 3,
      avtoritet: 0,
      stress: 10,
      health: 100,
      heat: 0,
      cash: 12000, // 120 рублей, мелочь в кармане
      zone: 'dvor',
      tile: { x: 16, y: 20 },
    },
    habar: [],
    businesses: [],
    companions: [],
    npc: {},
    quests: {},
    counters: {},
    brigade: [],
    territories: [],
    justice: { raids: 0, lastRaidDay: 0, courtDay: null },
    outcome: 'active',
    owned: [],
    garage: [],
    activeVehicle: null,
    equipment: {},
    world: { touched: [] },
    log: [{ day: 1, minute: 0, text: 'Проснулся в хате. Мамка гремит на кухне.' }],
  };
}
